import './AgentOrderApproval.scss';
import { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Page } from '@Components/Page';
import { Button } from '@Components/Button';
import { Appearance, Intent, Size } from '@Components/designConstants';
import { Spinner } from '@Components/Spinner';
import { OrderStatus } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import { awaitJSON, StreamingOrdersLambdaFetch } from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { ApprovedAgentOrder, PendingAgentOrder } from './agent-order-approval-constants';
import { SendOrderDialog } from '../StreamingBuying/SendOrderDialog';
import { SendVia } from '../StreamingBuying/send-order-utils';
import {
  agentOrderStatusLabel,
  canSendAgentOrder,
  isApprovedAgentOrder
} from './agent-order-approval-utils';

// Orders the media-planning agent drafts arrive under the shared automation
// identity. This screen is the human governance gate, and it covers three
// human decisions in the buying lifecycle:
//   - DRAFT (status 0): approve (POST /agent_orders/approve, which records who and
//     when; the order stays a draft) or reject (delete).
//   - APPROVED but not yet sent: it can still be rolled back (cancelled -> 30) OR
//     sent to the network. Orders committed to status 20 before approvals were
//     recorded count as approved.
// Send opens Streaming Buying's send view in a dialog (SendOrderDialog): it makes the IO, which
// the server's send guard refuses for an unapproved agent order, then a person reviews and sends
// the email to the network. It's enabled only on an approved draft.
const AGENT_ORDERS_PENDING = '/agent_orders/pending';
// Derived from the canonical bpm-types enum so FE/BE agree on the taxonomy
// rather than re-declaring independent literals (DEV-23563 #5). Typed `number`
// because order.status is a plain number off the API (avoids
// no-unsafe-enum-comparison on the equality checks below).
const CANCELED_STATUS: number = OrderStatus.CANCELED;
export const QUERY_KEY = 'agentOrdersPending';

// The two human decisions on an agent draft, shared with the Agentic Platform's Plan & Buy.
// Approve records who approved it without sending it; reject deletes it. The server lets only a
// person approve, checks their company access, and refuses committing an agent order instead.
export const approveAgentOrder = async (id: number): Promise<ApprovedAgentOrder> =>
  awaitJSON<ApprovedAgentOrder>(
    await StreamingOrdersLambdaFetch('/agent_orders/approve', { method: 'POST', body: { id } })
  );

export const rejectAgentOrder = (id: number) =>
  StreamingOrdersLambdaFetch('/orders', { method: 'DELETE', params: { id } });

// Returns this company's automation-created orders awaiting a decision (draft or
// committed-but-unsent), scoped SERVER-SIDE via the required ?company= param (the
// endpoint enforces validateCompanyAccess). Keyed on company via React Query so it
// refetches on company change. (DEV-23563 #3/#6 — no client-side filtering of a
// cross-company list.)
export const fetchPendingAgentOrders = async (company: string): Promise<PendingAgentOrder[]> => {
  const res = await StreamingOrdersLambdaFetch(AGENT_ORDERS_PENDING, { params: { company } });
  const { pending } = await awaitJSON<{ pending: PendingAgentOrder[] }>(res);
  return pending ?? [];
};

interface OrderActionsProps {
  order: PendingAgentOrder;
  busy: boolean;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  onCancel: (id: number) => void;
  onSend: (order: PendingAgentOrder) => void;
}

// A single flight on a drafted insertion order (the read-only fields we preview).
interface IoFlight {
  id: number;
  platform?: string;
  dsp?: string;
  startDate?: string;
  endDate?: string;
  spend?: number;
  cpm?: number;
  impressions?: number;
  canceled?: boolean;
}

interface IoOrderDetail {
  id: number;
  network: string;
  status: number;
  flights?: IoFlight[];
}

// READ-ONLY order detail (GET /order = getOrder). This is NOT create_streaming_order
// — it fetches the drafted IO's flights so we can preview them in-app; it sends
// nothing.
const fetchIoOrderDetail = async (id: number): Promise<IoOrderDetail> => {
  const res = await StreamingOrdersLambdaFetch('/order', { params: { id } });
  return awaitJSON<IoOrderDetail>(res);
};

const usd0 = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

// The preview body — fetched lazily when the modal opens. Early returns for the
// loading / error / empty states keep the render flat.
const IoFlightsBody = ({ order }: { order: PendingAgentOrder }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['agentOrderIoPreview', order.id],
    queryFn: () => fetchIoOrderDetail(order.id)
  });
  if (isLoading) return <Spinner />;
  if (isError) {
    return <p className='agent-order-approval__io-note'>Could not load the IO detail.</p>;
  }
  const flights = (data?.flights ?? []).filter(f => !f.canceled);
  if (flights.length === 0) {
    return <p className='agent-order-approval__io-note'>No flights on this draft yet.</p>;
  }
  return (
    <table className='agent-order-approval__io-flights'>
      <thead>
        <tr>
          <th>Placement</th>
          <th>Flight</th>
          <th>Impressions</th>
          <th>CPM</th>
          <th>Spend</th>
        </tr>
      </thead>
      <tbody>
        {flights.map(flight => (
          <tr key={flight.id}>
            <td>{flight.platform || flight.dsp || order.network}</td>
            <td>
              {flight.startDate} – {flight.endDate}
            </td>
            <td>{(flight.impressions ?? 0).toLocaleString('en-US')}</td>
            <td>${(flight.cpm ?? 0).toFixed(2)}</td>
            <td>{usd0(flight.spend ?? 0)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

interface IoPdfOrder {
  networkPDFText?: string;
  orderPDFText?: string;
  firstName?: string;
  lastName?: string;
  stationName?: string;
}

interface IoPdfData {
  order?: IoPdfOrder;
}

// READ-ONLY IO paperwork (GET /orders/pdf = getPDFData). Returns the network/order
// PDF boilerplate (the frequency note) and the network's rep contact — the same data
// the legacy "Generate PDF" page renders. Read-only; sends nothing.
const fetchIoPdfData = async (id: number): Promise<IoPdfData> => {
  const res = await StreamingOrdersLambdaFetch('/orders/pdf', { params: { id } });
  return awaitJSON<IoPdfData>(res);
};

// Frequency note + rep contact for the drafted IO, fetched lazily. Both fields are
// optional per network, so this renders nothing until a network has PDF text and/or a
// contact on file — additive, never an error state.
const IoNotesBody = ({ order }: { order: PendingAgentOrder }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['agentOrderIoPdf', order.id],
    queryFn: () => fetchIoPdfData(order.id)
  });
  if (isLoading || isError) return undefined;
  const detail = data?.order;
  if (!detail) return undefined;
  const freqNote = detail.orderPDFText || detail.networkPDFText;
  const repName = [detail.firstName, detail.lastName].filter(Boolean).join(' ');
  if (!freqNote && !repName) return undefined;
  return (
    <dl className='agent-order-approval__io-meta'>
      {freqNote ? (
        <div className='agent-order-approval__io-meta-row'>
          <dt>IO notes</dt>
          <dd>{freqNote}</dd>
        </div>
      ) : undefined}
      {repName ? (
        <div className='agent-order-approval__io-meta-row'>
          <dt>Rep contact</dt>
          <dd>{repName}</dd>
        </div>
      ) : undefined}
    </dl>
  );
};

// In-app, READ-ONLY preview of the drafted insertion order — rendered from the
// read endpoint (GET /order), never the send path. Replaces the old deep-link to
// the legacy Streaming Buying "Generate PDF" page, which navigated away and shared
// the same backend as send. Opening this preview can never send or activate an IO.
const IoPreview = ({ order }: { order: PendingAgentOrder }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        appearance={Appearance.OUTLINED}
        size={Size.SM}
        data-testid={`view-io-${order.id}`}
        onClick={() => {
          setOpen(true);
          Mixpanel.track(MxE.ModalOpened, {
            page: PAGE_NAMES.AGENT_ORDER_APPROVALS,
            modal: 'io_preview'
          });
        }}
      >
        View IO
      </Button>
      <Modal show={open} onHide={() => setOpen(false)} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            Draft IO · {order.network} · #{order.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='agent-order-approval__io-note'>
            {agentOrderStatusLabel(order)} — read-only preview, rendered from the order record. Held
            for human review; viewing it here sends nothing.
          </p>
          {open ? <IoFlightsBody order={order} /> : undefined}
          {open ? <IoNotesBody order={order} /> : undefined}
        </Modal.Body>
      </Modal>
    </>
  );
};

const OrderActions = ({
  order,
  busy,
  onApprove,
  onReject,
  onCancel,
  onSend
}: OrderActionsProps) => {
  if (isApprovedAgentOrder(order)) {
    return (
      <>
        <IoPreview order={order} />
        <Button
          appearance={Appearance.OUTLINED}
          size={Size.SM}
          disabled={busy}
          data-testid={`cancel-button-${order.id}`}
          onClick={() => onCancel(order.id)}
        >
          Cancel
        </Button>
        <Button
          size={Size.SM}
          disabled={busy || !canSendAgentOrder(order)}
          data-testid={`send-button-${order.id}`}
          title={
            canSendAgentOrder(order)
              ? 'Emails the approved insertion order to the network — a real, outbound send.'
              : 'Committed before approvals were recorded, so it has no approval to send under.'
          }
          onClick={() => onSend(order)}
        >
          Send
        </Button>
      </>
    );
  }

  return (
    <>
      <IoPreview order={order} />
      <Button
        size={Size.SM}
        disabled={busy}
        data-testid={`approve-button-${order.id}`}
        onClick={() => onApprove(order.id)}
      >
        Approve
      </Button>
      <Button
        appearance={Appearance.OUTLINED}
        size={Size.SM}
        disabled={busy}
        data-testid={`reject-button-${order.id}`}
        onClick={() => onReject(order.id)}
      >
        Reject
      </Button>
      <Button
        appearance={Appearance.OUTLINED}
        size={Size.SM}
        disabled
        data-testid={`send-button-${order.id}`}
        title='Send is enabled only after an order is approved. A draft cannot be sent to a network.'
      >
        Send
      </Button>
    </>
  );
};

interface OrderRowProps {
  order: PendingAgentOrder;
  busy: boolean;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  onCancel: (id: number) => void;
  onSend: (order: PendingAgentOrder) => void;
}

const OrderRow = ({ order, busy, onApprove, onReject, onCancel, onSend }: OrderRowProps) => (
  <tr data-testid={`agent-order-row-${order.id}`}>
    <td>{order.id}</td>
    <td>{order.network}</td>
    <td>{order.company}</td>
    <td>{agentOrderStatusLabel(order)}</td>
    <td>
      <div className='agent-order-approval__actions'>
        <OrderActions
          order={order}
          busy={busy}
          onApprove={onApprove}
          onReject={onReject}
          onCancel={onCancel}
          onSend={onSend}
        />
      </div>
    </td>
  </tr>
);

interface BodyProps {
  orders: PendingAgentOrder[] | undefined;
  loading: boolean;
  actingId: number | undefined;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  onCancel: (id: number) => void;
  onSend: (order: PendingAgentOrder) => void;
}

const AgentOrderApprovalBody = ({
  orders,
  loading,
  actingId,
  onApprove,
  onReject,
  onCancel,
  onSend
}: BodyProps) => {
  if (loading) {
    return <Spinner />;
  }
  if (!orders || orders.length === 0) {
    return (
      <div className='agent-order-approval__empty' data-testid='agent-order-approval-empty'>
        No agent-drafted orders awaiting review.
      </div>
    );
  }
  return (
    <table className='agent-order-approval__table' data-testid='agent-order-approval-table'>
      <thead>
        <tr>
          <th>Order</th>
          <th>Network</th>
          <th>Company</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {orders.map(o => (
          <OrderRow
            key={o.id}
            order={o}
            busy={actingId === o.id}
            onApprove={onApprove}
            onReject={onReject}
            onCancel={onCancel}
            onSend={onSend}
          />
        ))}
      </tbody>
    </table>
  );
};

interface AgentOrderApprovalPanelProps {
  company: string;
}

// Embeddable governance panel: the pending-orders table plus the approve / reject
// / cancel / send actions, without the <Page> chrome. The route below wraps it in
// a Page; the Elevate demo tab embeds it directly so the "stop at Send" beat
// happens in-flow. Takes `company` as a prop instead of reading the route.
export const AgentOrderApprovalPanel = ({ company }: AgentOrderApprovalPanelProps) => {
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const queryClient = useQueryClient();

  // Track the single in-flight order id so only its row's buttons disable,
  // rather than locking the whole table on any action.
  const [actingId, setActingId] = useState<number>();
  const [sendingOrder, setSendingOrder] = useState<PendingAgentOrder>();
  // Stable: the send view's effects close it through this.
  const closeSend = useCallback(() => setSendingOrder(undefined), []);

  const {
    data: orders,
    isLoading,
    error
  } = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: [QUERY_KEY, company],
    queryFn: () => fetchPendingAgentOrders(company),
    enabled: Boolean(company)
  });

  // Surface load failures through the shared error modal (React Query has no
  // catch to hook into), keeping the body to loading/empty/loaded states.
  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load pending orders",
        message: `Failed to load pending orders: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  // Local row patch instead of refetching the whole list after each action
  // (DEV-23563 #6): approve marks the row approved in place; reject, cancel,
  // and send drop it from the pending list. Avoids an extra list round-trip on
  // every action (previously an unscoped, cross-company fetch).
  const markOrderApproved = ({ id, approvedBy, approvedAt }: ApprovedAgentOrder) =>
    queryClient.setQueryData<PendingAgentOrder[]>([QUERY_KEY, company], prev =>
      (prev ?? []).map(o =>
        o.id === id ? { ...o, approved_by: approvedBy, approved_at: approvedAt } : o
      )
    );
  const removeOrder = (id: number) =>
    queryClient.setQueryData<PendingAgentOrder[]>([QUERY_KEY, company], prev =>
      (prev ?? []).filter(o => o.id !== id)
    );

  const approveMutation = useMutation({
    mutationFn: approveAgentOrder,
    onSuccess: approved => markOrderApproved(approved),
    onError: (e: Error, id) =>
      setError({ message: `Failed to approve order ${id}. ${e.message}`, reportError: e }),
    onSettled: () => setActingId(undefined)
  });

  const rejectMutation = useMutation({
    mutationFn: rejectAgentOrder,
    onSuccess: (_res, id) => removeOrder(id),
    onError: (e: Error, id) =>
      setError({ message: `Failed to reject order ${id}. ${e.message}`, reportError: e }),
    onSettled: () => setActingId(undefined)
  });

  // Roll back a committed-but-unsent order: set it to Canceled (30) so it can
  // never be sent. Reuses the generic status transition, like approve.
  const cancelMutation = useMutation({
    mutationFn: (id: number) =>
      StreamingOrdersLambdaFetch('/orders/status', {
        method: 'POST',
        body: { id, status: CANCELED_STATUS }
      }),
    onSuccess: (_res, id) => removeOrder(id),
    onError: (e: Error, id) =>
      setError({ message: `Failed to cancel order ${id}. ${e.message}`, reportError: e }),
    onSettled: () => setActingId(undefined)
  });

  const handleApprove = async (id: number) => {
    try {
      await setAreYouSure({
        title: 'Approve this insertion order?',
        message: `Approving records that you approved agent-drafted order ${id}, and it becomes eligible to send. Any later edit clears the approval. This does NOT send it to the network.`,
        okayText: 'Approve'
      });
    } catch {
      return;
    }
    setActingId(id);
    approveMutation.mutate(id);
    Mixpanel.track(MxE.ActionTriggered, {
      page: PAGE_NAMES.AGENT_ORDER_APPROVALS,
      action: 'approve'
    });
  };

  const handleReject = async (id: number) => {
    try {
      await setAreYouSure({
        intent: Intent.ERROR,
        title: 'Reject this insertion order?',
        message: `Rejecting permanently deletes agent-drafted order ${id}.`,
        okayText: 'Reject'
      });
    } catch {
      return;
    }
    setActingId(id);
    rejectMutation.mutate(id);
    Mixpanel.track(MxE.ActionTriggered, {
      page: PAGE_NAMES.AGENT_ORDER_APPROVALS,
      action: 'reject'
    });
  };

  const handleCancel = async (id: number) => {
    try {
      await setAreYouSure({
        title: 'Cancel this approved order?',
        message: `Cancelling rolls back approved order ${id} — it is voided and will NOT be sent to the network.`,
        okayText: 'Cancel order'
      });
    } catch {
      return;
    }
    setActingId(id);
    cancelMutation.mutate(id);
    Mixpanel.track(MxE.ActionTriggered, {
      page: PAGE_NAMES.AGENT_ORDER_APPROVALS,
      action: 'cancel'
    });
  };

  const handleSent = (id: number) => {
    removeOrder(id);
    Mixpanel.track(MxE.ActionTriggered, { page: PAGE_NAMES.AGENT_ORDER_APPROVALS, action: 'send' });
  };

  // Company info resolves asynchronously (cid is '' until it loads); treat that
  // as loading so we never fetch/filter against an empty company.
  const loading = !company || isLoading;

  return (
    <div className='agent-order-approval'>
      <p className='agent-order-approval__intro'>
        Insertion orders drafted by the media-planning agent, awaiting human review. Approve a draft
        to make it eligible to send; reject to delete it. An approved order can still be cancelled
        (rolled back), or sent: Send opens the IO and an email to the network to review before it
        goes out.
      </p>
      <AgentOrderApprovalBody
        orders={orders}
        loading={loading}
        actingId={actingId}
        onApprove={handleApprove}
        onReject={handleReject}
        onCancel={handleCancel}
        onSend={setSendingOrder}
      />
      {sendingOrder && (
        <SendOrderDialog
          orderId={sendingOrder.id}
          network={sendingOrder.network}
          via={SendVia.AGENT_ORDER_APPROVALS}
          onHide={closeSend}
          onSent={() => handleSent(sendingOrder.id)}
        />
      )}
    </div>
  );
};

// Route host: the /:company/streaming/agent-approvals page. Resolves the company
// from the route and wraps the shared panel in Page chrome.
export const AgentOrderApproval = () => {
  const { cid: company } = useCompanyInfo();
  return (
    <Page
      title={PAGE_NAMES.AGENT_ORDER_APPROVALS}
      pageType={PAGE_NAMES.AGENT_ORDER_APPROVALS}
      isLoading={!company}
    >
      <AgentOrderApprovalPanel company={company} />
    </Page>
  );
};
